import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Select, Table, Tag, Input, message } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import { tenantApi, sqlConsoleApi } from "../../services/api";
import { useEnvironment } from "../../context/EnvironmentContext";

const { TextArea } = Input;

const PLACEHOLDER = [
  "-- Read-only. Qualify tables with ${schema_name} — it is replaced with the selected tenant's schema.",
  "SELECT TOP 20 s.named_id, s.status, s.completed_on, m.total_score_achieved",
  "FROM ${schema_name}.clv2_schedule s",
  "LEFT JOIN ${schema_name}.clv2_execution_metadata m ON m.schedule_id = s.id",
  "WHERE s.status = 'Completed'",
  "ORDER BY s.completed_on DESC",
].join("\n");

function formatCell(value) {
  if (value === null || value === undefined) {
    return <span className="opacity-50">NULL</span>;
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

export default function SqlConsolePage() {
  const { selectedEnv, changeEnv, envOptions } = useEnvironment();
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [sql, setSql] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    setTenants([]);
    setTenant(null);
    tenantApi
      .getAllForEnv(selectedEnv)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data.tenants || [];
        setTenants(list);
      })
      .catch((err) => {
        if (!cancelled) message.error(err.response?.data?.error || "Could not load tenants");
      })
      .finally(() => {
        if (!cancelled) setTenantsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedEnv]);

  const tenantOptions = useMemo(
    () =>
      tenants.map((t) => ({
        value: t.name,
        label: t.subdomain && t.subdomain !== t.name ? `${t.name} (${t.subdomain})` : t.name,
      })),
    [tenants]
  );

  const run = async () => {
    if (!tenant) { message.warning("Select a tenant first."); return; }
    if (!sql.trim()) { message.warning("Type a query first."); return; }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await sqlConsoleApi.run(tenant, sql);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || { code: "request_failed", message: err.message });
    } finally {
      setRunning(false);
    }
  };

  const columns = useMemo(() => {
    if (!result || !result.columns) return [];
    return result.columns.map((c) => ({
      title: c,
      dataIndex: c,
      key: c,
      ellipsis: true,
      render: (v) => formatCell(v),
    }));
  }, [result]);

  const dataSource = useMemo(() => {
    if (!result || !result.rows) return [];
    return result.rows.map((r, i) => ({ ...r, __key: i }));
  }, [result]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">SQL Console</h1>
        <p className="page-subtitle">
          Read-only. Queries run against the selected env/tenant through the AI agent's connection. Audited.
        </p>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          className="w-full md:w-40"
          value={selectedEnv}
          onChange={changeEnv}
          options={envOptions}
        />
        <Select
          className="w-full md:w-72"
          placeholder={tenantsLoading ? "Loading tenants…" : "Tenant"}
          loading={tenantsLoading}
          value={tenant}
          onChange={setTenant}
          options={tenantOptions}
          showSearch
          optionFilterProp="label"
        />
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={running}
          disabled={!tenant || !sql.trim()}
          onClick={run}
        >
          Run
        </Button>
      </div>

      <TextArea
        className="font-[monospace] !text-[12px]"
        rows={8}
        placeholder={PLACEHOLDER}
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") run();
        }}
      />
      <p className="mt-1 mb-4 text-[12px] opacity-60">
        SELECT only — anything else is rejected by the agent's validator. Qualify every table with{" "}
        <code>{"${schema_name}"}</code> (substituted with the selected tenant's schema, exactly as the agent does).
        Results cap at 1000 rows, 30s timeout. Ctrl/Cmd+Enter runs.
      </p>

      {error && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message={error.message || "Query failed"}
          description={
            Array.isArray(error.violations) && error.violations.length > 0 ? (
              <ul className="m-0 pl-5">
                {error.violations.map((v, i) => (
                  <li key={i}>
                    <strong>{v.code}</strong>: {v.message}
                    {v.suggestion ? <div className="text-[12px] opacity-70">{v.suggestion}</div> : null}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
      )}

      {result && (
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px]">
            <Tag color="blue">{result.rowCount} row{result.rowCount === 1 ? "" : "s"}</Tag>
            <Tag>{result.ms} ms</Tag>
            {result.truncated && <Tag color="orange">truncated at {result.rowCap}</Tag>}
            <span className="opacity-60">{selectedEnv} · {tenant}</span>
          </div>
          {result.rowCount === 0 ? (
            <Alert type="info" showIcon message="Query ran successfully and returned 0 rows." />
          ) : (
            <Table
              size="small"
              columns={columns}
              dataSource={dataSource}
              rowKey="__key"
              scroll={{ x: true }}
              pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
            />
          )}
        </div>
      )}
    </div>
  );
}
